import React from 'react';
import { SocialUser } from '../../services/socialService';
import logo from '../../assets/logo.png';

interface FriendsWithoutStudyShareCardProps {
  friendsWithoutStudy: SocialUser[];
  cardRef: React.RefObject<HTMLDivElement>;
}

export default function FriendsWithoutStudyShareCard({ friendsWithoutStudy, cardRef }: FriendsWithoutStudyShareCardProps) {
  return (
    <div className="fixed top-[200vh] left-[200vw] pointer-events-none z-[-100]">
      <div ref={cardRef} style={{
        width: '1080px',
        height: '1920px',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '120px 80px',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '800px', height: '800px',
          background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute', bottom: '-200px', left: '-200px',
          width: '800px', height: '800px',
          background: 'radial-gradient(circle, rgba(168,85,247,0.2) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />

        {/* Header with Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '80px', zIndex: 1 }}>
          <img referrerPolicy="no-referrer" src={logo} alt="LinguaLearn Logo" style={{ width: '200px', height: '200px', marginBottom: '24px' }} crossOrigin="anonymous" />
          <h1 style={{
            fontSize: '72px',
            fontWeight: 900,
            letterSpacing: '-2px',
            margin: 0,
            color: '#e0e7ff',
            textShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>LinguaLearn</h1>
        </div>

        {/* Title */}
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          padding: '32px 64px',
          borderRadius: '32px',
          marginBottom: '80px',
          border: '2px solid rgba(255,255,255,0.2)',
          boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
          zIndex: 1
        }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: 800,
            margin: 0,
            textAlign: 'center',
            color: '#fff',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>🚨 Missing in Action 🚨</h2>
          <p style={{
            fontSize: '32px',
            color: 'rgba(255,255,255,0.8)',
            textAlign: 'center',
            margin: '16px 0 0 0',
            fontWeight: 500
          }}>Friends who need to study!</p>
        </div>

        {/* Friends List */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          width: '100%',
          maxWidth: '920px', // Maximize available width
          zIndex: 1,
          flex: 1
        }}>
          {friendsWithoutStudy.slice(0, 7).map((user, idx) => {
            const userId = user.id || String(idx);
            return (
              <div key={userId} style={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(255,255,255,0.05)',
                padding: '32px 40px', // Generous padding but leaves room for text
                borderRadius: '32px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                gap: '32px'
              }}>
                <div style={{
                  width: '110px', height: '110px', // Slightly smaller avatar
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                  padding: '4px',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                  flexShrink: 0
                }}>
                  <div style={{
                    width: '100%', height: '100%',
                    borderRadius: '50%',
                    background: '#1e1b4b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '44px', fontWeight: 800,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {/* Always render initials as a fallback layer behind the image */}
                    <span style={{ position: 'absolute', zIndex: 1 }}>
                      {user.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                    
                    {/* Image layer on top */}
                    {user.profilePhotoUrl && (
                      <img 
                        referrerPolicy="no-referrer" 
                        src={user.profilePhotoUrl} 
                        alt={user.username} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 2 }} 
                        crossOrigin="anonymous" 
                      />
                    )}
                  </div>
                </div>
                
                <div style={{ flex: 1, minWidth: 0, paddingRight: '16px' }}>
                  <p style={{ 
                    fontSize: '40px', 
                    fontWeight: 800, 
                    margin: '0 0 12px 0', 
                    lineHeight: 1.1,
                    wordBreak: 'break-word' // Allow wrapping instead of cutting
                  }}>
                    {user.username}
                  </p>
                  <p style={{ 
                    fontSize: '26px', 
                    color: '#fca5a5', 
                    fontWeight: 600, 
                    margin: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px' 
                  }}>
                    <span>⏳</span> {user.daysWithoutStudy} days without study
                  </p>
                </div>
              </div>
            );
          })}
          {friendsWithoutStudy.length > 7 && (
            <div style={{ textAlign: 'center', fontSize: '32px', fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginTop: '24px' }}>
              + {friendsWithoutStudy.length - 7} more friends
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 1,
          background: 'rgba(0,0,0,0.3)',
          padding: '24px 64px',
          borderRadius: '999px',
          backdropFilter: 'blur(10px)'
        }}>
          <p style={{
            fontSize: '36px',
            fontWeight: 800,
            margin: 0,
            letterSpacing: '2px'
          }}>lingualearn.web.app</p>
          <p style={{
            fontSize: '24px',
            color: 'rgba(255,255,255,0.7)',
            margin: '8px 0 0 0',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '4px'
          }}>Join us and start learning</p>
        </div>
      </div>
    </div>
  );
}
